// =====================================================
// PAGE ADMIN: Liste des Coupons
// Date: 2025-11-28
// Description: Gestion des coupons de réduction multi-modules
// =====================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Trash2, 
  Edit, 
  Eye, 
  ToggleLeft, 
  ToggleRight,
  Tag,
  Percent,
  Calendar,
  Users,
  ShoppingBag,
  BookOpen,
  Heart,
  Loader2,
  Copy,
  CheckCircle,
  XCircle,
  BarChart3,
  Check,
  X,
  Copy as CopyIcon
} from 'lucide-react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { toast } from 'sonner';
import { 
  getAllCoupons, 
  deleteCoupon, 
  toggleCouponStatus,
  updateCoupon,
  getGlobalCouponStats,
  type Coupon,
  type ModuleType,
  type DiscountType
} from '../../../services/couponService';

const moduleIcons: Record<ModuleType, React.ElementType> = {
  shop: ShoppingBag,
  elearning: BookOpen,
  psy: Heart,
  dating: Heart
};

const moduleLabels: Record<ModuleType, string> = {
  shop: 'Boutique',
  elearning: 'E-learning',
  psy: 'Psy',
  dating: 'Rencontres'
};

const moduleColors: Record<ModuleType, string> = {
  shop: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  elearning: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  psy: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  dating: 'bg-pink-500/20 text-pink-400 border-pink-500/30'
};

const NEVER_EXPIRES_DATE = '2099-12-31';

// Type pour l'édition inline
type EditingField = { id: string | null; value: string };
type EditingNumber = { id: string | null; value: number };

export default function CouponList() {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);
  const [filterModule, setFilterModule] = useState<ModuleType | undefined>(undefined);
  const [stats, setStats] = useState<{
    total_coupons: number;
    active_coupons: number;
    total_usages: number;
    total_discount_given: number;
  } | null>(null);

  // États pour l'édition inline
  const [editingCode, setEditingCode] = useState<EditingField>({ id: null, value: '' });
  const [editingName, setEditingName] = useState<EditingField>({ id: null, value: '' });
  const [editingValue, setEditingValue] = useState<EditingNumber>({ id: null, value: 0 });
  const [editingValidFrom, setEditingValidFrom] = useState<EditingField>({ id: null, value: '' });
  const [editingValidUntil, setEditingValidUntil] = useState<EditingField>({ id: null, value: '' });
  const [editingUsageLimit, setEditingUsageLimit] = useState<EditingField>({ id: null, value: '' });

  useEffect(() => {
    loadCoupons();
    loadStats();
  }, [filterActive, filterModule]);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const data = await getAllCoupons({
        is_active: filterActive,
        module: filterModule
      });
      setCoupons(data);
    } catch (error) {
      console.error('Erreur chargement coupons:', error);
      toast.error('Erreur lors du chargement des coupons');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await getGlobalCouponStats();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const handleDelete = async (id: string, code: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le coupon "${code}" ?`)) return;
    
    try {
      await deleteCoupon(id);
      toast.success('Coupon supprimé avec succès');
      loadCoupons();
      loadStats();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await toggleCouponStatus(id, !currentStatus);
      toast.success(currentStatus ? 'Coupon désactivé' : 'Coupon activé');
      loadCoupons();
      loadStats();
    } catch (error) {
      console.error('Erreur toggle status:', error);
      toast.error('Erreur lors du changement de statut');
    }
  };

  // Fonction générique pour mettre à jour un coupon inline
  const handleInlineUpdate = async (id: string, updates: Partial<Coupon>) => {
    try {
      await updateCoupon(id, updates);
      toast.success('Coupon mis à jour');
      loadCoupons();
      loadStats();
      return true;
    } catch (error: any) {
      console.error('Erreur mise à jour:', error);
      if (error.message?.includes('duplicate') || error.code === '23505') {
        toast.error('Ce code existe déjà');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
      return false;
    }
  };

  // Handlers pour chaque champ éditable
  const saveCode = async (id: string) => {
    if (!editingCode.value.trim()) {
      toast.error('Le code ne peut pas être vide');
      return;
    }
    const success = await handleInlineUpdate(id, { code: editingCode.value.toUpperCase().trim() });
    if (success) setEditingCode({ id: null, value: '' });
  };

  const saveName = async (id: string) => {
    if (!editingName.value.trim()) {
      toast.error('Le nom ne peut pas être vide');
      return;
    }
    const success = await handleInlineUpdate(id, { name: editingName.value.trim() });
    if (success) setEditingName({ id: null, value: '' });
  };

  const saveValue = async (id: string, discountType: DiscountType) => {
    if (editingValue.value <= 0) {
      toast.error('La valeur doit être positive');
      return;
    }
    if (discountType === 'percentage' && editingValue.value > 100) {
      toast.error('Le pourcentage ne peut pas dépasser 100%');
      return;
    }
    const success = await handleInlineUpdate(id, { discount_value: editingValue.value });
    if (success) setEditingValue({ id: null, value: 0 });
  };

  const saveValidFrom = async (id: string) => {
    if (!editingValidFrom.value) {
      toast.error('La date de début est requise');
      return;
    }
    const success = await handleInlineUpdate(id, { 
      valid_from: new Date(editingValidFrom.value).toISOString() 
    });
    if (success) setEditingValidFrom({ id: null, value: '' });
  };

  const saveValidUntil = async (id: string) => {
    if (!editingValidUntil.value) {
      toast.error('La date de fin est requise');
      return;
    }
    const success = await handleInlineUpdate(id, { 
      valid_until: new Date(editingValidUntil.value + 'T23:59:59').toISOString() 
    });
    if (success) setEditingValidUntil({ id: null, value: '' });
  };

  const saveUsageLimit = async (id: string) => {
    const value = editingUsageLimit.value.trim();
    const limit = value === '' ? null : parseInt(value);
    if (limit !== null && (isNaN(limit) || limit < 0)) {
      toast.error('Limite invalide');
      return;
    }
    const success = await handleInlineUpdate(id, { usage_limit: limit });
    if (success) setEditingUsageLimit({ id: null, value: '' });
  };

  const setNeverExpires = async (id: string) => {
    const success = await handleInlineUpdate(id, { 
      valid_until: new Date(NEVER_EXPIRES_DATE + 'T23:59:59').toISOString() 
    });
    if (success) setEditingValidUntil({ id: null, value: '' });
  };

  // Dupliquer un coupon
  const handleDuplicate = async (coupon: Coupon) => {
    try {
      // Générer un nouveau code unique
      let newCode = `${coupon.code}-COPY`;
      let suffix = 1;
      while (coupons.some(c => c.code === newCode)) {
        newCode = `${coupon.code}-COPY${suffix}`;
        suffix++;
      }

      const { createCoupon } = await import('../../../services/couponService');
      
      await createCoupon({
        code: newCode,
        name: `${coupon.name} (copie)`,
        description: coupon.description,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value,
        min_purchase_amount: coupon.min_purchase_amount,
        max_discount_amount: coupon.max_discount_amount,
        usage_limit: coupon.usage_limit,
        usage_limit_per_user: coupon.usage_limit_per_user,
        valid_from: new Date().toISOString(),
        valid_until: coupon.valid_until,
        is_active: false, // Désactivé par défaut pour éviter les erreurs
        applies_to_modules: coupon.applies_to_modules,
        created_by: null
      });

      toast.success(`Coupon dupliqué : ${newCode}`);
      loadCoupons();
      loadStats();
    } catch (error: any) {
      console.error('Erreur duplication:', error);
      if (error.message?.includes('duplicate') || error.code === '23505') {
        toast.error('Un coupon avec ce code existe déjà');
      } else {
        toast.error('Erreur lors de la duplication');
      }
    }
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code "${code}" copié !`);
  };

  const filteredCoupons = coupons.filter(coupon => 
    coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coupon.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isExpired = (validUntil: string) => new Date(validUntil) < new Date();
  const isNotStarted = (validFrom: string) => new Date(validFrom) > new Date();
  const isNeverExpires = (validUntil: string) => new Date(validUntil).getFullYear() >= 2099;

  return (
    <AdminLayout>
      <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Tag className="w-7 h-7 text-[#D4AF37]" />
            Gestion des Coupons
          </h1>
          <p className="text-gray-400 mt-1">Gérez vos codes promo pour Shop, E-learning et Psy</p>
        </div>
        <button
          onClick={() => navigate('/admin/coupons/new')}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus className="w-5 h-5" />
          Nouveau coupon
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Tag className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total coupons</p>
                <p className="text-xl font-bold text-white">{stats.total_coupons}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Coupons actifs</p>
                <p className="text-xl font-bold text-white">{stats.active_coupons}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Utilisations</p>
                <p className="text-xl font-bold text-white">{stats.total_usages}</p>
              </div>
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
                <Percent className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Réductions totales</p>
                <p className="text-xl font-bold text-white">{stats.total_discount_given.toLocaleString('fr-FR')} FCFA</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par code ou nom..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            />
          </div>
          
          {/* Filter by status */}
          <select
            value={filterActive === undefined ? 'all' : filterActive ? 'active' : 'inactive'}
            onChange={(e) => {
              const val = e.target.value;
              setFilterActive(val === 'all' ? undefined : val === 'active');
            }}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>
          
          {/* Filter by module */}
          <select
            value={filterModule || 'all'}
            onChange={(e) => {
              const val = e.target.value;
              setFilterModule(val === 'all' ? undefined : val as ModuleType);
            }}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          >
            <option value="all">Tous les modules</option>
            <option value="shop">Boutique</option>
            <option value="elearning">E-learning</option>
            <option value="psy">Psy</option>
          </select>
        </div>
      </div>

      {/* Coupons Table */}
      <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : filteredCoupons.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">Aucun coupon trouvé</p>
            <button
              onClick={() => navigate('/admin/coupons/new')}
              className="mt-4 text-[#D4AF37] hover:underline"
            >
              Créer votre premier coupon
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Code</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Réduction</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Modules</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Validité</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Utilisations</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Statut</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredCoupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-4">
                      {/* Code - éditable */}
                      {editingCode.id === coupon.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editingCode.value}
                            onChange={(e) => setEditingCode({ id: coupon.id, value: e.target.value.toUpperCase() })}
                            className="w-28 px-2 py-1 bg-white/10 border border-white/20 rounded text-white font-mono text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveCode(coupon.id);
                              if (e.key === 'Escape') setEditingCode({ id: null, value: '' });
                            }}
                          />
                          <button onClick={() => saveCode(coupon.id)} className="p-1 text-green-400 hover:text-green-300">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingCode({ id: null, value: '' })} className="p-1 text-gray-400 hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span 
                            className="font-mono font-bold text-white bg-white/10 px-2 py-1 rounded cursor-pointer hover:bg-white/20"
                            onClick={() => setEditingCode({ id: coupon.id, value: coupon.code })}
                            title="Cliquez pour modifier"
                          >
                            {coupon.code}
                          </span>
                          <button
                            onClick={() => copyToClipboard(coupon.code)}
                            className="p-1 text-gray-400 hover:text-white"
                            title="Copier"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      )}

                      {/* Nom - éditable */}
                      {editingName.id === coupon.id ? (
                        <div className="flex items-center gap-1 mt-1">
                          <input
                            type="text"
                            value={editingName.value}
                            onChange={(e) => setEditingName({ id: coupon.id, value: e.target.value })}
                            className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveName(coupon.id);
                              if (e.key === 'Escape') setEditingName({ id: null, value: '' });
                            }}
                          />
                          <button onClick={() => saveName(coupon.id)} className="p-1 text-green-400 hover:text-green-300">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingName({ id: null, value: '' })} className="p-1 text-gray-400 hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <p 
                          className="text-sm text-gray-200 mt-1 font-medium cursor-pointer hover:bg-white/5 px-1 rounded"
                          onClick={() => setEditingName({ id: coupon.id, value: coupon.name })}
                          title="Cliquez pour modifier"
                        >
                          {coupon.name}
                        </p>
                      )}

                      {coupon.description && (
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                          {coupon.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {/* Valeur de réduction - éditable */}
                      {editingValue.id === coupon.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={editingValue.value}
                            onChange={(e) => setEditingValue({ id: coupon.id, value: parseFloat(e.target.value) || 0 })}
                            className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
                            autoFocus
                            min="0"
                            step={coupon.discount_type === 'percentage' ? '1' : '100'}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveValue(coupon.id, coupon.discount_type);
                              if (e.key === 'Escape') setEditingValue({ id: null, value: 0 });
                            }}
                          />
                          <span className="text-gray-400 text-sm">
                            {coupon.discount_type === 'percentage' ? '%' : 'FCFA'}
                          </span>
                          <button onClick={() => saveValue(coupon.id, coupon.discount_type)} className="p-1 text-green-400 hover:text-green-300">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingValue({ id: null, value: 0 })} className="p-1 text-gray-400 hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="flex items-center gap-2 cursor-pointer hover:bg-white/5 px-1 rounded"
                          onClick={() => setEditingValue({ id: coupon.id, value: coupon.discount_value })}
                          title="Cliquez pour modifier"
                        >
                          <Percent className="w-4 h-4 text-[#D4AF37]" />
                          <span className="text-white font-medium">
                            {coupon.discount_type === 'percentage' 
                              ? `${coupon.discount_value}%`
                              : `${coupon.discount_value.toLocaleString('fr-FR')} FCFA`
                            }
                          </span>
                        </div>
                      )}
                      {coupon.min_purchase_amount > 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                          Min: {coupon.min_purchase_amount.toLocaleString('fr-FR')} FCFA
                        </p>
                      )}
                      {coupon.max_discount_amount !== null && coupon.discount_type === 'percentage' && (
                        <p className="text-xs text-gray-400 mt-1">
                          Plafond: {coupon.max_discount_amount.toLocaleString('fr-FR')} FCFA
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {coupon.applies_to_modules.map((module) => {
                          const Icon = moduleIcons[module];
                          return (
                            <span
                              key={module}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${moduleColors[module]}`}
                            >
                              <Icon className="w-3 h-3" />
                              {moduleLabels[module]}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm space-y-1">
                        {/* Date de début - éditable */}
                        {editingValidFrom.id === coupon.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="date"
                              value={editingValidFrom.value}
                              onChange={(e) => setEditingValidFrom({ id: coupon.id, value: e.target.value })}
                              className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveValidFrom(coupon.id);
                                if (e.key === 'Escape') setEditingValidFrom({ id: null, value: '' });
                              }}
                            />
                            <button onClick={() => saveValidFrom(coupon.id)} className="p-0.5 text-green-400 hover:text-green-300">
                              <Check className="w-3 h-3" />
                            </button>
                            <button onClick={() => setEditingValidFrom({ id: null, value: '' })} className="p-0.5 text-gray-400 hover:text-white">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <p 
                            className="text-white cursor-pointer hover:bg-white/5 px-1 rounded inline-block"
                            onClick={() => setEditingValidFrom({ id: coupon.id, value: coupon.valid_from.split('T')[0] })}
                            title="Cliquez pour modifier"
                          >
                            {formatDate(coupon.valid_from)}
                          </p>
                        )}

                        {/* Date de fin - éditable */}
                        {editingValidUntil.id === coupon.id ? (
                          <div className="flex items-center gap-1 flex-wrap">
                            <input
                              type="date"
                              value={editingValidUntil.value}
                              onChange={(e) => setEditingValidUntil({ id: coupon.id, value: e.target.value })}
                              className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveValidUntil(coupon.id);
                                if (e.key === 'Escape') setEditingValidUntil({ id: null, value: '' });
                              }}
                            />
                            <button onClick={() => saveValidUntil(coupon.id)} className="p-0.5 text-green-400 hover:text-green-300">
                              <Check className="w-3 h-3" />
                            </button>
                            <button onClick={() => setEditingValidUntil({ id: null, value: '' })} className="p-0.5 text-gray-400 hover:text-white">
                              <X className="w-3 h-3" />
                            </button>
                            <button 
                              onClick={() => setNeverExpires(coupon.id)} 
                              className="text-xs text-[#D4AF37] hover:underline ml-1"
                              title="Définir comme n'expirant jamais"
                            >
                              ∞
                            </button>
                          </div>
                        ) : (
                          <p 
                            className="text-gray-400 cursor-pointer hover:bg-white/5 px-1 rounded inline-block"
                            onClick={() => setEditingValidUntil({ 
                              id: coupon.id, 
                              value: isNeverExpires(coupon.valid_until) ? '' : coupon.valid_until.split('T')[0] 
                            })}
                            title="Cliquez pour modifier"
                          >
                            {isNeverExpires(coupon.valid_until) ? "N'expire jamais" : `→ ${formatDate(coupon.valid_until)}`}
                          </p>
                        )}
                      </div>
                      {!isNeverExpires(coupon.valid_until) && isExpired(coupon.valid_until) && (
                        <span className="text-xs text-red-400">Expiré</span>
                      )}
                      {isNotStarted(coupon.valid_from) && (
                        <span className="text-xs text-yellow-400">Pas encore actif</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {/* Limite d'utilisation - éditable */}
                      {editingUsageLimit.id === coupon.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-white">{coupon.usage_count}/</span>
                          <input
                            type="text"
                            value={editingUsageLimit.value}
                            onChange={(e) => setEditingUsageLimit({ id: coupon.id, value: e.target.value })}
                            placeholder="∞"
                            className="w-12 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveUsageLimit(coupon.id);
                              if (e.key === 'Escape') setEditingUsageLimit({ id: null, value: '' });
                            }}
                          />
                          <button onClick={() => saveUsageLimit(coupon.id)} className="p-1 text-green-400 hover:text-green-300">
                            <Check className="w-4 h-4" />
                          </button>
                          <button onClick={() => setEditingUsageLimit({ id: null, value: '' })} className="p-1 text-gray-400 hover:text-white">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="text-white cursor-pointer hover:bg-white/5 px-1 rounded inline-block"
                          onClick={() => setEditingUsageLimit({ 
                            id: coupon.id, 
                            value: coupon.usage_limit?.toString() || '' 
                          })}
                          title="Cliquez pour modifier la limite"
                        >
                          {coupon.usage_count}
                          {coupon.usage_limit ? (
                            <span className="text-gray-400">/{coupon.usage_limit}</span>
                          ) : (
                            <span className="text-gray-400">/∞</span>
                          )}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        Limite / utilisateur: {coupon.usage_limit_per_user || 0}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleToggleStatus(coupon.id, coupon.is_active)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                          coupon.is_active 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}
                      >
                        {coupon.is_active ? (
                          <>
                            <ToggleRight className="w-4 h-4" />
                            Actif
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4" />
                            Inactif
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/admin/coupons/${coupon.id}`)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/coupons/${coupon.id}/stats`)}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                          title="Statistiques"
                        >
                          <BarChart3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(coupon)}
                          className="p-2 text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors"
                          title="Dupliquer"
                        >
                          <CopyIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon.id, coupon.code)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}
